import { useEffect, useState } from 'react'
import { DEVICE_TYPE, DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'

/**
 * Header-specific device type hook that responds to viewport changes.
 * Uses matchMedia for reliable updates when the window is resized.
 * Use only for header-related layout decisions (Header, MatterOverview).
 */
export function useHeaderDeviceType() {
  const [deviceType, setDeviceType] = useState<(typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE]>(() => {
    if (typeof window === 'undefined') return DEVICE_TYPE.DESKTOP
    const w = window.innerWidth
    if (w <= DEVICE_TYPE_BREAKPOINTS.MOBILE) return DEVICE_TYPE.MOBILE
    if (w < DEVICE_TYPE_BREAKPOINTS.DESKTOP) return DEVICE_TYPE.TABLET
    return DEVICE_TYPE.DESKTOP
  })

  useEffect(() => {
    const mobileQuery = window.matchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.MOBILE}px)`)
    const tabletQuery = window.matchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.DESKTOP - 1}px)`)

    const updateDeviceType = () => {
      if (mobileQuery.matches) {
        setDeviceType(DEVICE_TYPE.MOBILE)
      } else if (tabletQuery.matches) {
        setDeviceType(DEVICE_TYPE.TABLET)
      } else {
        setDeviceType(DEVICE_TYPE.DESKTOP)
      }
    }

    mobileQuery.addEventListener('change', updateDeviceType)
    tabletQuery.addEventListener('change', updateDeviceType)
    updateDeviceType()
    return () => {
      mobileQuery.removeEventListener('change', updateDeviceType)
      tabletQuery.removeEventListener('change', updateDeviceType)
    }
  }, [])

  return deviceType
}

export { DEVICE_TYPE }
